import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Plus, Minus } from "lucide-react";
import { api, imgUrl, typeLabel } from "@/lib/api";
import { useVisibleCategories } from "@/lib/useCategories";
import { Reveal } from "@/components/site/Reveal";
import { ReviewQuote } from "@/components/site/ReviewCard";
import { Html, Btn, TextLink, Head, Img, SmartLink } from "./Parts";
import { ReviewForm, ContactForm, StudioData, ContactInfo } from "./Forms";

const useGet = (url, params) => {
  const [data, setData] = useState(null);
  const key = JSON.stringify(params || {});
  useEffect(() => { if (url) api.get(url, { params }).then((r) => setData(r.data)).catch(() => setData([])); }, [url, key]); // eslint-disable-line react-hooks/exhaustive-deps
  return data;
};

export const Reviews = ({ d }) => {
  const items = useGet("/public/reviews", d.event_type ? { event_type: d.event_type } : {}) || [];
  const shown = d.limit ? items.slice(0, d.limit) : items;
  if (d.show_form) return (
    <div className="container-x pb-32 grid grid-cols-12 gap-x-6 gap-y-16" data-testid="block-reviews">
      <div className="col-span-12 lg:col-span-7 space-y-16">
        {shown.map((r, k) => <Reveal key={r.id}><ReviewQuote r={r} i={k} large={k === 0} /></Reveal>)}
        {!items.length && d.empty_text && <p className="font-serif italic text-2xl text-mute" data-testid="reviews-empty">{d.empty_text}</p>}
      </div>
      <div className="col-span-12 lg:col-span-4 lg:col-start-9 lg:sticky lg:top-32 self-start bg-sabbia border-t-2 border-oro p-8 sm:p-10" data-testid="review-form"><ReviewForm /></div>
    </div>);
  if (!items.length && !d.empty_text) return null;
  return (
    <section className={`${d.background === "cream" ? "bg-sabbia" : ""} py-24 sm:py-32`} data-testid="block-reviews"><div className="container-x">
      <Head d={items.length ? d : { ...d, link_text: "" }} />
      {!items.length ? <p className="font-serif italic text-2xl text-mute" data-testid="home-reviews-empty">{d.empty_text}</p> : (
        <div className="grid grid-cols-12 gap-x-6 gap-y-12">
          {shown.map((r, k) => <Reveal key={r.id} className={k === 0 && d.limit !== 2 ? "col-span-12 lg:col-span-7" : "col-span-12 sm:col-span-6 lg:col-span-5"}><ReviewQuote r={r} i={k} large={k === 0 && d.limit !== 2} /></Reveal>)}
        </div>)}
    </div></section>
  );
};

export const ServiceVisual = ({ s, className = "aspect-[4/5]" }) => (s.cover ? <Img src={s.cover} alt={`${s.title} - Studio Fotografico Santarelli`} className={`w-full object-cover ${className}`} />
  : <div className={`w-full bg-sabbia border border-line flex items-end p-6 ${className}`} aria-hidden="true"><span className="font-serif italic font-light text-5xl text-pietra">{s.title}</span></div>);

export const Services = ({ d }) => {
  const items = useGet("/public/services") || [];
  if (d.layout === "index") return (
    <div className="container-x pb-28 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-20" data-testid="block-services">
      {items.map((s, k) => (
        <Reveal key={s.id} delay={(k % 3) * 0.12} className={k % 3 === 1 ? "lg:mt-20" : k % 3 === 2 ? "lg:mt-10" : ""}>
          <article data-testid={`service-entry-${s.slug}`}>
            <Link to={`/${s.page_slug}`} className="block overflow-hidden img-zoom"><ServiceVisual s={s} /></Link>
            <h2 className="mt-7 font-serif font-light text-[38px] leading-tight tracking-[-0.025em]">{s.title}</h2>
            <p className="mt-4 text-[15px] font-light text-stoneish leading-[1.85]">{s.intro}</p>
            <TextLink text={d.link_text || "Scopri il servizio"} to={`/${s.page_slug}`} className="mt-6" testid={`service-discover-${s.slug}`} />
          </article>
        </Reveal>))}
    </div>);
  return (
    <section className="bg-sabbia py-24 sm:py-32" data-testid="home-services"><div className="container-x">
      <Head d={d} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10 sm:gap-6 mb-20" data-testid="home-services-photos">
        {items.filter((s) => s.cover).slice(0, 4).map((s, k) => (
          <Reveal key={s.id} delay={k * 0.1} className={k % 2 ? "lg:mt-20" : ""}>
            <Link to={`/${s.page_slug}`} className="group block" data-testid={`home-service-photo-${s.slug}`}>
              <div className="overflow-hidden img-zoom aspect-[4/5]"><Img src={s.cover} alt={`${s.title} - Studio Fotografico Santarelli`} className="w-full h-full object-cover" /></div>
              <p className="mt-5 font-serif text-[26px] font-light group-hover:text-oliva transition-colors duration-500">{s.title}</p>
              {s.intro && <p className="mt-2 text-[13px] font-light text-stoneish leading-[1.7]">{s.intro}</p>}
            </Link>
          </Reveal>))}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-10">
        {items.map((s) => <Link key={s.id} to={`/${s.page_slug}`} className="group flex items-baseline justify-between gap-4 py-6 border-b border-pietra transition-[border-color] duration-500 hover:border-oro" data-testid={`home-service-${s.slug}`}>
          <span className="font-serif text-[29px] font-light group-hover:text-oliva transition-colors duration-500">{s.title}</span><ArrowRight size={15} strokeWidth={1.4} className="text-oro shrink-0 transition-transform duration-500 group-hover:translate-x-1" /></Link>)}
      </div>
    </div></section>
  );
};

export const Faq = ({ d }) => {
  const remote = useGet("/public/faqs") || [];
  const [open, setOpen] = useState(d.items?.length ? -1 : 0);
  const items = d.items?.length ? d.items.map((i) => ({ question: i.title, answer: i.text })) : remote.filter((f) => !d.category || f.category === d.category);
  if (!items.length && !d.button_text) return null;
  return (
    <section className="container-x py-20 pb-32 grid grid-cols-12" data-testid="block-faq">
      <div className={`col-span-12 lg:col-span-8 ${d.eyebrow ? "" : "lg:col-start-3"}`}>
        {d.eyebrow && <p className="eyebrow mb-6 flex items-center gap-4"><span className="rule-gold" />{d.eyebrow}</p>}
        <Html inline as="h2" html={d.title} className="font-serif font-light text-4xl sm:text-[50px] leading-[1.06] tracking-[-0.025em] mb-12" />
        {items.map((f, k) => (
          <div key={k} className={`border-t transition-colors duration-500 ${open === k ? "border-oro" : "border-line"}`}>
            <button onClick={() => setOpen(open === k ? -1 : k)} aria-expanded={open === k} className="w-full flex justify-between items-start gap-6 py-8 text-left group" data-testid={`faq-q-${k}`}>
              <span className={`font-serif font-light text-[23px] sm:text-[30px] leading-snug transition-colors duration-500 ${open === k ? "text-oliva" : "group-hover:text-oliva"}`}>{f.question}</span>
              <span className="mt-2 text-oro transition-transform duration-500 group-hover:rotate-90">{open === k ? <Minus size={18} strokeWidth={1.4} /> : <Plus size={18} strokeWidth={1.4} />}</span>
            </button>
            {open === k && <p className="pb-10 text-[15.5px] font-light text-stoneish leading-[1.85] max-w-2xl animate-in fade-in slide-in-from-top-1 duration-500" data-testid={`faq-a-${k}`}>{f.answer}</p>}
          </div>))}
        {d.button_text && <div className="border-t border-line pt-14"><Btn text={d.button_text} to={d.button_link} testid="faq-contact-btn" /></div>}
      </div>
    </section>
  );
};

export const Cta = ({ d, page }) => {
  const services = useGet(d.show_related ? "/public/services" : null, {}) || [];
  if (d.image) return (
    <section className="relative h-[75svh] min-h-[520px] overflow-hidden grain" data-testid="home-cta">
      <Img src={d.image} alt={d.image_alt} className="absolute inset-0 w-full h-full object-cover" /><div className="absolute inset-0 bg-gradient-to-r from-ink/75 via-ink/45 to-ink/20" />
      <div className="relative z-[2] h-full container-x grid grid-cols-12 content-center text-seta"><Reveal className="col-span-12 lg:col-span-7">
        {d.eyebrow && <p className="eyebrow !text-orolight mb-7 flex items-center gap-4"><span className="w-10 h-px bg-oro" />{d.eyebrow}</p>}
        <Html inline as="h2" html={d.title} className="font-serif font-light text-[42px] sm:text-7xl leading-[1.02] tracking-[-0.03em] [&_em]:italic [&_em]:text-orolight" />
        {d.text && <p className="mt-7 text-[15.5px] font-light text-seta/85 max-w-md leading-[1.85]">{d.text}</p>}
        <div className="mt-11 flex flex-wrap gap-4"><Btn text={d.button_text} to={d.button_link} gold testid="cta-button" /><Btn text={d.button2_text} to={d.button2_link} light outline testid="cta-button-2" /></div>
      </Reveal></div>
    </section>);
  const related = d.show_related ? services.filter((s) => s.page_slug !== page?.slug).slice(0, 3) : [];
  if (d.show_studio) return (
    <section className="container-x py-24 sm:py-36 grid grid-cols-12 gap-y-14 gap-x-6 items-center" data-testid="block-cta-studio">
      <Reveal className="col-span-12 lg:col-span-6">
        {d.eyebrow && <p className="eyebrow mb-7 flex items-center gap-4"><span className="rule-gold" />{d.eyebrow}</p>}
        <Html inline as="h2" html={d.title} className="font-serif font-light text-4xl sm:text-[58px] leading-[1.04] tracking-[-0.03em] [&_em]:italic [&_em]:text-oliva" />
        {d.text && <p className="mt-7 text-[15.5px] font-light text-stoneish leading-[1.85] max-w-lg">{d.text}</p>}
        <div className="mt-11 flex flex-wrap gap-4"><Btn text={d.button_text} to={d.button_link} testid="cta-button" /><Btn text={d.button2_text} to={d.button2_link} outline testid="cta-button-2" /></div>
      </Reveal>
      <Reveal delay={0.15} className="col-span-12 lg:col-span-5 lg:col-start-8 bg-sabbia border-t-2 border-oro p-8 sm:p-10 space-y-8"><StudioData /></Reveal>
    </section>);
  const simple = !d.title && !d.text && !d.eyebrow;
  return (
    <section className={`container-x grid lg:grid-cols-2 gap-10 items-end ${simple ? "pb-24" : "py-20"} ${d.show_related ? "border-t border-pietra" : ""}`} data-testid="block-cta">
      <div>
        {d.eyebrow && <p className="eyebrow mb-4">{d.eyebrow}</p>}
        <Html inline as="h2" html={d.title} className={`font-serif font-light ${d.show_related ? "text-4xl sm:text-5xl tracking-[-0.02em]" : "text-[26px] leading-snug max-w-lg"}`} />
        {d.text && <p className="mt-4 text-[15px] text-stoneish leading-[1.8] max-w-lg">{d.text}</p>}
        {(d.show_related || simple) && <div className={`flex flex-wrap gap-3 ${simple ? "" : "mt-8"}`}><Btn text={d.button_text} to={d.button_link} testid="cta-button" /><Btn text={d.button2_text} to={d.button2_link} outline testid="cta-button-2" /></div>}
      </div>
      {!d.show_related && !simple && <div className="flex flex-wrap gap-3 lg:justify-end"><Btn text={d.button_text} to={d.button_link} testid="cta-button" /><Btn text={d.button2_text} to={d.button2_link} outline testid="cta-button-2" /></div>}
      {related.length > 0 && <nav aria-label="Servizi correlati"><p className="eyebrow mb-4">Altri servizi</p>{related.map((r) => <Link key={r.slug} to={`/${r.page_slug}`} className="group flex justify-between py-4 border-b border-line font-serif font-light text-2xl transition-colors duration-500 hover:text-oliva hover:border-oro" data-testid={`related-${r.slug}`}>{r.title}<ArrowRight size={16} strokeWidth={1.4} className="text-oro transition-transform duration-500 group-hover:translate-x-1" /></Link>)}</nav>}
    </section>
  );
};

export const Contact = () => (
  <div className="container-x pb-32 sm:pb-40 grid grid-cols-12 gap-x-6 gap-y-16" data-testid="block-contact">
    <div className="col-span-12 lg:col-span-7"><ContactForm /></div>
    <div className="col-span-12 lg:col-span-4 lg:col-start-9 lg:mt-8"><ContactInfo /></div>
  </div>
);

export const Portfolio = ({ d }) => {
  const [cat, setCat] = useState(d.category || "");
  const visible = useVisibleCategories();
  const picked = d.filters?.length ? visible.filter((c) => d.filters.includes(c.value)) : visible;
  const stories = useGet("/public/galleries", cat ? { category: cat } : {});
  if (d.mode === "list") return (
    <div className="container-x pb-32" data-testid="block-portfolio">
      {!d.category && picked.length > 1 && <div className="flex gap-8 overflow-x-auto scrollbar-none border-y border-line py-6 mb-14">{[{ value: "", plural: "Tutte le storie" }, ...picked].map((c) => (
        <button key={c.value} onClick={() => setCat(c.value)} data-testid={`portfolio-filter-${c.value || "tutti"}`}
          className={`text-[12px] uppercase tracking-[0.16em] whitespace-nowrap pb-1 border-b transition-colors duration-500 ${cat === c.value ? "text-ink border-oro" : "text-mute border-transparent hover:text-oliva"}`}>{c.plural}</button>))}</div>}
      {stories && !stories.length && <p className="py-24 font-serif italic font-light text-3xl text-mute max-w-xl" data-testid="portfolio-empty">{d.empty_text}</p>}
      <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 lg:gap-10 [&>*]:mb-6 lg:[&>*]:mb-14">
        {(stories || []).map((g, k) => <ArchiveCard key={g.id} g={g} k={k} />)}
      </div>
    </div>);
  const all = stories || [];
  const featured = (all.filter((g) => g.featured).length ? all.filter((g) => g.featured) : all).slice(0, 6);
  return (
    <section className="container-x py-24 sm:py-32" data-testid="home-featured">
      <Head d={d} />
      {stories && !featured.length ? <p className="font-serif italic font-light text-3xl text-mute" data-testid="home-featured-empty">{d.empty_text}</p> : (
        <div className="grid grid-cols-12 gap-x-6 lg:gap-x-10 gap-y-16 sm:gap-y-20">{featured.map((g, k) => {
          const big = k % 2 === 0;
          return (
            <Reveal key={g.id} delay={(k % 2) * 0.1} className={big ? "col-span-12 lg:col-span-7" : "col-span-12 sm:col-span-8 sm:col-start-5 lg:col-span-4 lg:col-start-9 lg:self-end"}>
              <Link to={`/portfolio/${g.slug}`} className="group block" data-testid={`featured-${g.slug}`}>
                <div className={`overflow-hidden img-zoom ${big ? "aspect-[3/2]" : "aspect-[4/5]"}`}><Img src={g.cover || g.preview?.[0]} alt={`Storia fotografica: ${g.title}`} eager={k < 2} className="w-full h-full object-cover" /></div>
                <div className="mt-6 border-t border-line pt-5 transition-colors duration-500 group-hover:border-oro">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-oro">{String(k + 1).padStart(2, "0")} — {typeLabel(g.category)}</p>
                  <h3 className="mt-3 font-serif font-light text-3xl sm:text-[40px] leading-[1.05] tracking-[-0.025em] transition-colors duration-500 group-hover:text-oliva">{g.title}</h3>
                  <p className="mt-2 text-[13px] text-mute">{[g.location, g.season].filter(Boolean).join(" · ")}</p>
                  {g.intro && <p className="mt-4 text-[14.5px] font-light text-stoneish leading-[1.8] max-w-md line-clamp-2">{g.intro}</p>}
                  <span className="mt-6 inline-flex items-center gap-2.5 text-[12px] uppercase tracking-[0.16em]">Apri la storia<span className="text-mute normal-case tracking-normal">· {g.photo_count} foto</span><ArrowRight size={14} strokeWidth={1.4} className="text-oro transition-transform duration-500 group-hover:translate-x-1" /></span>
                </div>
              </Link>
            </Reveal>);
        })}</div>)}
    </section>
  );
};

const ArchiveCard = ({ g, k }) => (
  <div className="break-inside-avoid fade-up" style={{ animationDelay: `${Math.min(k, 9) * 0.07}s` }}>
    <Link to={`/portfolio/${g.slug}`} className="group block" data-testid={`gallery-card-${g.slug}`}>
      <div className={`relative overflow-hidden img-zoom ${[/* ritmo variabile */ "aspect-[3/4]", "aspect-[4/5]", "aspect-[1/1]", "aspect-[3/4]", "aspect-[4/3]", "aspect-[4/5]"][k % 6]}`}>
        <Img src={g.cover} alt={`Storia fotografica: ${g.title}`} eager={k < 3} className="w-full h-full object-cover" />
        <span className="absolute top-4 left-4 bg-seta/92 backdrop-blur px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-ink">{typeLabel(g.category)}</span>
        <span className="absolute inset-0 bg-ink/0 group-hover:bg-ink/15 transition-colors duration-700" />
      </div>
      <div className="mt-5 flex items-start justify-between gap-5 border-t border-line pt-4 transition-colors duration-500 group-hover:border-oro">
        <div>
          <h2 className="font-serif font-light text-[30px] leading-[1.08] tracking-[-0.025em] transition-colors duration-500 group-hover:text-oliva">{g.title}</h2>
          <p className="mt-2 text-[12.5px] text-mute">{[g.location, g.season].filter(Boolean).join(" · ")}{g.photo_count ? ` · ${g.photo_count} foto` : ""}</p>
        </div>
        <ArrowRight size={16} strokeWidth={1.4} className="mt-2 text-oro shrink-0 transition-transform duration-500 group-hover:translate-x-1" />
      </div>
    </Link>
  </div>
);

export const Categories = ({ d }) => {
  const items = useGet("/public/services") || [];
  const [hover, setHover] = useState(0);
  if (!items.length) return null;
  const preview = items[hover] || items[0];
  return (
    <section className="py-24 sm:py-32 bg-sabbia" data-testid="block-categories"><div className="container-x grid grid-cols-12 gap-x-6 lg:gap-x-10 gap-y-12">
      <Reveal className="col-span-12 lg:col-span-7">
        {d.eyebrow && <p className="eyebrow mb-8 flex items-center gap-4"><span className="rule-gold" />{d.eyebrow}</p>}
        <Html inline as="h2" html={d.title} className="font-serif font-light text-4xl sm:text-[52px] leading-[1.05] tracking-[-0.03em] mb-12 [&_em]:italic [&_em]:text-oliva" />
        <ul>{items.map((s, k) => (
          <li key={s.id} onMouseEnter={() => setHover(k)}>
            <Link to={`/${s.page_slug}`} data-testid={`category-row-${s.slug}`}
              className="group flex flex-wrap sm:flex-nowrap items-baseline gap-x-5 sm:gap-x-8 gap-y-2 py-6 border-t border-pietra transition-[border-color,padding-left] duration-500 hover:border-oro hover:pl-2">
              <span className="text-[11px] tracking-[0.18em] text-oro w-7 shrink-0">{String(k + 1).padStart(2, "0")}</span>
              <span className="min-w-0 flex-1 font-serif font-light text-[26px] sm:text-[42px] leading-none tracking-[-0.025em] transition-colors duration-500 group-hover:text-oliva">{s.title}</span>
              <span className="hidden sm:block max-w-[14rem] text-[13px] font-light text-mute leading-snug">{s.intro}</span>
              <ArrowRight size={16} strokeWidth={1.4} className="text-oro shrink-0 transition-transform duration-500 group-hover:translate-x-1" />
            </Link>
          </li>))}</ul>
      </Reveal>
      <Reveal delay={0.2} className="hidden lg:block col-span-4 col-start-9 self-end">
        <div className="aspect-[3/4] overflow-hidden bg-sabbiascura">
          {preview.cover ? <Img key={preview.cover} src={preview.cover} alt={preview.title} eager className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-end p-7"><span className="font-serif italic font-light text-4xl text-pietra">{preview.title}</span></div>}
        </div>
        <p className="mt-5 text-[12px] uppercase tracking-[0.18em] text-oliva">{preview.title}</p>
      </Reveal>
    </div></section>
  );
};

export const Catalog = ({ d }) => {
  const items = useGet("/public/catalog") || [];
  return (
    <section className="container-x pb-24 sm:pb-32" data-testid="block-catalog"><Head d={d} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">{items.map((it, k) => (
        <Reveal key={it.id} delay={(k % 3) * 0.1} className={`border-t border-oro/40 pt-8 ${k % 3 === 1 ? "lg:mt-16" : ""}`}>
          <div data-testid={`catalog-item-${k}`}><div className="overflow-hidden img-zoom mb-7"><Img src={it.image} alt={it.name} className="w-full aspect-[4/3] object-cover" /></div>
            <h2 className="font-serif font-light text-[32px] leading-tight tracking-[-0.02em]">{it.name}</h2><p className="mt-4 text-[15px] font-light text-stoneish leading-[1.85]">{it.description}</p></div>
        </Reveal>))}</div>
    </section>
  );
};

export const Team = ({ d }) => {
  const team = useGet("/public/team") || [];
  if (!team.length) return null;
  return (
    <section className="container-x py-24 sm:py-32" data-testid="studio-team">{d.eyebrow && <p className="eyebrow mb-14 flex items-center gap-4"><span className="rule-gold" />{d.eyebrow}</p>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-16">{team.map((t, k) => (
        <Reveal key={t.id} delay={(k % 4) * 0.1} className={k % 2 ? "lg:mt-16" : ""}>
        <figure data-testid={`team-member-${t.id}`}>
          {t.photo
            ? <div className="overflow-hidden img-zoom"><Img src={t.photo} alt={`${t.name}, ${t.role}`} className="w-full aspect-[4/5] object-cover" /></div>
            : <div className="w-full aspect-[4/5] bg-sabbia border border-line flex items-center justify-center" aria-hidden="true">
                <span className="font-serif font-light text-5xl text-oliva/50 tracking-[0.06em]">{t.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}</span>
              </div>}
          <figcaption className="mt-6 border-t border-oro/40 pt-5"><p className="font-serif font-light text-[26px] leading-tight tracking-[-0.02em]">{t.name}</p><p className="mt-2 text-[12px] uppercase tracking-[0.14em] text-oliva">{t.role}</p>{t.bio && <p className="mt-4 text-[14.5px] font-light text-stoneish leading-[1.8]">{t.bio}</p>}</figcaption>
        </figure></Reveal>))}</div>
    </section>
  );
};

export { SmartLink };
